import { ArrowDownUp, X } from 'lucide-react';
import {
  ITSM_UNASSIGNED,
  type ItsmBoardFacets, type ItsmBoardSort, type ItsmSlaFilter,
} from '../../services/api';
import { compactField, ghostButton, muted, ring } from './itsmUi';

export interface BoardQuery {
  sort: ItsmBoardSort;
  order: '' | 'asc' | 'desc';
  orgId: string;
  queue: string;
  assignee: string;
  sla: '' | ItsmSlaFilter;
  unassignedService: boolean;
  openOnly: boolean;
}

const SORTS: { id: ItsmBoardSort; label: string; asc: string; desc: string }[] = [
  { id: 'sla', label: 'SLA', asc: 'breaching first', desc: 'breaching last' },
  { id: 'priority', label: 'Priority', asc: 'most urgent first', desc: 'least urgent first' },
  { id: 'age', label: 'Age', asc: 'oldest first', desc: 'newest first' },
  { id: 'updated', label: 'Last updated', asc: 'least recent first', desc: 'most recent first' },
];

const DEFAULT_ORDER: Record<ItsmBoardSort, 'asc' | 'desc'> = {
  sla: 'asc', priority: 'asc', age: 'asc', updated: 'desc',
};

/**
 * The service board's sort and filters (PO46-1280). Every control is a labelled
 * field and every change goes to the server: the order shown is the server's,
 * over the whole queue, never a re-sort of one page.
 */
export default function BoardFilters({ value, facets, onChange }: {
  value: BoardQuery;
  facets: ItsmBoardFacets | null;
  onChange: (patch: Partial<BoardQuery>) => void;
}) {
  const order = value.order || DEFAULT_ORDER[value.sort];
  const sortDef = SORTS.find((s) => s.id === value.sort) || SORTS[0];
  const filtered = !!(value.orgId || value.queue || value.assignee || value.sla || value.unassignedService);

  return (
    <div role="group" aria-label="Sort and filter the board" className="flex flex-wrap items-end gap-3 mb-4">
      <div>
        <label htmlFor="board-sort" className={`block text-[11px] font-medium ${muted} mb-0.5`}>Sort by</label>
        <div className="flex items-center gap-1">
          <select id="board-sort" className={compactField} value={value.sort}
            onChange={(e) => onChange({ sort: e.target.value as ItsmBoardSort, order: '' })}>
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button type="button"
            onClick={() => onChange({ order: order === 'asc' ? 'desc' : 'asc' })}
            aria-label={`Order: ${order === 'asc' ? sortDef.asc : sortDef.desc}. Reverse it.`}
            title={order === 'asc' ? sortDef.asc : sortDef.desc}
            className={`p-1.5 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-accent ${ring}`}>
            <ArrowDownUp size={14} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div>
        <label htmlFor="board-org" className={`block text-[11px] font-medium ${muted} mb-0.5`}>Organisation</label>
        <select id="board-org" className={compactField} value={value.orgId}
          onChange={(e) => onChange({ orgId: e.target.value })}>
          <option value="">All organisations</option>
          {(facets?.orgs || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      <div>
        <label htmlFor="board-queue" className={`block text-[11px] font-medium ${muted} mb-0.5`}>Queue</label>
        <select id="board-queue" className={compactField} value={value.queue}
          onChange={(e) => onChange({ queue: e.target.value })}>
          <option value="">All queues</option>
          <option value={ITSM_UNASSIGNED}>No queue</option>
          {(facets?.queues || []).map((q) => <option key={q} value={q}>{facets?.queueNames?.[q] || q}</option>)}
        </select>
      </div>

      <div>
        <label htmlFor="board-assignee" className={`block text-[11px] font-medium ${muted} mb-0.5`}>Assignee</label>
        <select id="board-assignee" className={compactField} value={value.assignee}
          onChange={(e) => onChange({ assignee: e.target.value })}>
          <option value="">Anyone</option>
          <option value={ITSM_UNASSIGNED}>Unassigned</option>
          {(facets?.assignees || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div>
        <label htmlFor="board-sla" className={`block text-[11px] font-medium ${muted} mb-0.5`}>SLA</label>
        <select id="board-sla" className={compactField} value={value.sla}
          onChange={(e) => onChange({ sla: e.target.value as BoardQuery['sla'] })}>
          <option value="">Any</option>
          <option value="breached">Breached</option>
          <option value="at-risk">Breached or at risk</option>
          <option value="due">Clock running</option>
        </select>
      </div>

      <div className="flex flex-col gap-1 pb-0.5">
        <div className="flex items-center gap-1.5">
          <input id="board-open" type="checkbox" checked={value.openOnly}
            className={`h-4 w-4 rounded accent-accent ${ring}`}
            onChange={(e) => onChange({ openOnly: e.target.checked })} />
          <label htmlFor="board-open" className="text-xs text-slate-700 dark:text-slate-300">Open only</label>
        </div>
        <div className="flex items-center gap-1.5">
          <input id="board-noservice" type="checkbox" checked={value.unassignedService}
            className={`h-4 w-4 rounded accent-accent ${ring}`}
            onChange={(e) => onChange({ unassignedService: e.target.checked })} />
          <label htmlFor="board-noservice" className="text-xs text-slate-700 dark:text-slate-300">No service</label>
        </div>
      </div>

      {filtered && (
        <button type="button" className={`${ghostButton} flex items-center gap-1 pb-1.5`}
          onClick={() => onChange({ orgId: '', queue: '', assignee: '', sla: '', unassignedService: false })}>
          <X size={12} aria-hidden="true" /> Clear filters
        </button>
      )}
    </div>
  );
}
